import React, { useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useHistory } from 'react-router-dom';
import moment from 'moment-timezone';
import { AppConstants, PayedBy } from '../../../../shared/Constants';
import { AccountContext } from '../../../../components/AccountContext';

const i18n = defineMessages({
  dialogHeader: {
    id: 'subscription-expired-dialog.header',
    defaultMessage: 'Your NoLimit subscription has expired!',
  },
  firstParagraph: {
    id: 'subscription-expired-dialog.first-paragraph',
    defaultMessage: "Your organisation's NoLimit subscription expired on {expirationDate}.",
  },
  secondParagraph: {
    id: 'subscription-expired-dialog.second-paragraph',
    defaultMessage:
      'Until {ninetyDaysAfterExpiration}, you and other users have access to the existing data. However, new assessments, surveys etc. cannot be setup unless a new NoLimit subscription is added to the account.',
  },
  payedBySelfThirdParagraph: {
    id: 'subscription-expired-dialog.payed-by-self.third-paragraph',
    defaultMessage:
      "If you want to add a new NoLimit subscription, you must do so before {ninetyDaysAfterExpiration}. If you do not, the account and all it's data is irrevocably deleted on {ninetyDaysAfterExpiration}.",
  },
  payedByParentThirdParagraph: {
    id: 'subscription-expired-dialog.payed-by-parent.third-paragraph',
    defaultMessage:
      'If you want to add new NoLimit subscription, please contact your GPI representative in good time before {ninetyDaysAfterExpiration}:',
  },
  addNewSubscription: {
    id: 'subscription-expired-dialog.add-new-subscription',
    defaultMessage: 'Add new subscription',
  },
  continueWithoutNewSubscription: {
    id: 'subscription-expired-dialog.continue-without-new-subscription',
    defaultMessage: 'Continue without new subscription',
  },
  payedBySelfFourthParagraph: {
    id: 'subscription-expired-dialog.payed-by-self.fourth-paragraph',
    defaultMessage: 'If you need assistance, please contact your GPI representative:',
  },
  payedByParentFourthParagraph: {
    id: 'subscription-expired-dialog.payed-by-parent.fourth-paragraph',
    defaultMessage:
      'If a new subscription is not added before {ninetyDaysAfterExpiration}, the account and all its data is irrevocably deleted on {ninetyDaysAfterExpiration}.',
  },
  name: { id: 'attrs.name', defaultMessage: 'Name' },
  email: { id: 'attrs.email', defaultMessage: 'Email' },
  gotIt: {
    id: 'user-notification-dialog.button',
    defaultMessage: 'Got it',
  },
});

const SubscriptionExpiredDialog = ({ subscriptionInformation, gpiRepresentativeData, visible, hideDialog }) => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const currentAccount = useContext(AccountContext);

  if (!subscriptionInformation) return null;

  const { currentSubscription } = subscriptionInformation;

  if (!currentSubscription) return null;

  const { payedBy } = subscriptionInformation;

  const { endsOn: expirationDate, ninetyDaysAfterExpiration } = currentSubscription;

  const formattedExpirationDate = moment(expirationDate).format(AppConstants.DATE_INPUT_FORMAT);
  const formattedNinetyDaysAfterExpiration = moment(ninetyDaysAfterExpiration).format(AppConstants.DATE_INPUT_FORMAT);

  const diaglogHeader = <div className="dashboard-panel-header">{formatMessage(i18n.dialogHeader)}</div>;

  const PayedBySelfContent = () => {
    return (
      <>
        <p>{formatMessage(i18n.firstParagraph, { expirationDate: formattedExpirationDate })}</p>
        <p>{formatMessage(i18n.secondParagraph, { ninetyDaysAfterExpiration: formattedNinetyDaysAfterExpiration })}</p>
        <p className="font-weight-600">
          {formatMessage(i18n.payedBySelfThirdParagraph, {
            ninetyDaysAfterExpiration: formattedNinetyDaysAfterExpiration,
          })}
        </p>
        <div className="flex-center">
          <Button
            type="button"
            className="p-button-primary width-15"
            onClick={() => history.push(`/buy-own-subscription?accountId=${currentAccount}`)}
            label={formatMessage(i18n.addNewSubscription)}
          />
        </div>
        <br />
        <div className="flex-center">
          <Button
            type="button"
            className="p-button-primary width-15"
            onClick={hideDialog}
            label={formatMessage(i18n.continueWithoutNewSubscription)}
          />
        </div>
        <p>{formatMessage(i18n.payedBySelfFourthParagraph)}</p>
        <p>{`${formatMessage(i18n.name)}: ${gpiRepresentativeData.parentAccountOwnerFullName}`}</p>
        <p>{`${formatMessage(i18n.email)}: ${gpiRepresentativeData.parentAccountOwnerEmail}`}</p>
      </>
    );
  };

  const PayedByParentContent = () => {
    return (
      <>
        <p>{formatMessage(i18n.firstParagraph, { expirationDate: formattedExpirationDate })}</p>
        <p>{formatMessage(i18n.secondParagraph, { ninetyDaysAfterExpiration: formattedNinetyDaysAfterExpiration })}</p>
        <p className="font-weight-600">
          {formatMessage(i18n.payedByParentThirdParagraph, {
            ninetyDaysAfterExpiration: formattedNinetyDaysAfterExpiration,
          })}
        </p>
        <p>{`${formatMessage(i18n.name)}: ${gpiRepresentativeData.parentAccountOwnerFullName}`}</p>
        <p>{`${formatMessage(i18n.email)}: ${gpiRepresentativeData.parentAccountOwnerEmail}`}</p>
        <p>
          {formatMessage(i18n.payedByParentFourthParagraph, {
            ninetyDaysAfterExpiration: formattedNinetyDaysAfterExpiration,
          })}
        </p>
        <div className="flex-center">
          <Button type="button" className="p-button-primary" onClick={hideDialog} label={formatMessage(i18n.gotIt)} />
        </div>
      </>
    );
  };

  return (
    <Dialog visible={visible} closable={false} header={diaglogHeader} onHide={() => {}}>
      {payedBy === PayedBy.PAYED_BY_SELF && <PayedBySelfContent />}
      {payedBy === PayedBy.PAYED_BY_PARENT && <PayedByParentContent />}
    </Dialog>
  );
};

SubscriptionExpiredDialog.propTypes = {
  payedBy: PropTypes.string.isRequired,
  subscriptionInformation: PropTypes.objectOf(PropTypes.any).isRequired,
  gpiRepresentativeData: PropTypes.objectOf(PropTypes.any).isRequired,
  visible: PropTypes.bool.isRequired,
  hideDialog: PropTypes.func.isRequired,
};

export default SubscriptionExpiredDialog;
